/**
 * useCurrentUser — the portal's session probe, run on every mount.
 *
 * AXF-1013: this used to be `enabled: authStore.isAuthenticated && authStore.isInitialized`, which
 * meant it never ran for a staff member arriving on the shared cookie alone — the localStorage that
 * gated it was written only by the auth callback this ticket deletes. It is now unconditional, and
 * `data === null` is the answer "no session" rather than an error.
 */
import { useQuery } from '@tanstack/react-query'
import { authActions } from '@store/authStore'
import { gatewayClient } from '@infrastructure/api'
import { mapGatewayResponseToUser } from '@application/services'
import type { User } from '@domain/entities'
import { AUTH_QUERY_KEYS } from './queryKeys'

export const useCurrentUser = () => {
  return useQuery<User | null>({
    queryKey: AUTH_QUERY_KEYS.currentUser,
    queryFn: async () => {
      const probe = await gatewayClient.getCurrentUser()

      if (probe.status !== 'authenticated') {
        authActions.logout()
        return null
      }

      const user = mapGatewayResponseToUser(probe.raw)
      if (!user) {
        // A 200 the mapper rejects is a server contract change, not a signed-out user. Treated as
        // no session so the portal still renders something actionable, and logged by the mapper.
        authActions.logout()
        return null
      }

      authActions.setUser(user)
      return user
    },
    staleTime: 5 * 60 * 1000,
    // AXF-1013: ON, unlike every other query in this app. The probe is the portal's only way of
    // learning that a session has ended, and a staff member who signs out in AX WorkFlow and comes
    // back to this tab is precisely the AC-8 case. Re-probing on focus bounds how stale the answer
    // can get; the 401 path in `client.ts` covers the case where they never leave the tab.
    refetchOnWindowFocus: true,
    retry: false,
  })
}
